import { useEffect } from 'react'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { useIntlayer } from 'react-intlayer'
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  Edit,
  FlipHorizontal,
  ImageIcon,
  Palette,
  SendToBack,
  Shrink,
  Trash2,
} from 'lucide-react'
import { renderToString } from 'react-dom/server'

export type OnboardingTourProps = {
  enabled?: boolean
  onComplete?: () => void
  onSkip?: () => void
}

export const OnboardingTour = ({ enabled = true, onComplete, onSkip }: OnboardingTourProps) => {
  const intlData = useIntlayer('onboardingTour')

  const steps = intlData?.steps

  useEffect(() => {
    if (!enabled || !steps) return

    // Extract the 'key' property from React elements for the actual translation text
    const getText = (element: any): string => {
      if (typeof element === 'string') return element
      if (element?.key) return element.key
      if (element?.props?.children) return element.props.children
      return String(element)
    }

    // Build the HTML descriptions as strings first
    const toolbarDescription = `
      <div style="line-height: 1.6;">
        <p style="margin-bottom: 12px;">${getText(steps.toolbar.description)}</p>
        <ul style="margin-left: 20px; list-style-type: disc;">
          <li><strong>${getText(steps.toolbar.textLabel)}</strong> - ${getText(steps.toolbar.textDescription)}</li>
          <li><strong>${getText(steps.toolbar.imageLabel)}</strong> - ${getText(steps.toolbar.imageDescription)}</li>
          <li><strong>${getText(steps.toolbar.photoLabel)}</strong> - ${getText(steps.toolbar.photoDescription)}</li>
        </ul>
      </div>
    `

    const horizontalCenterIcon = <AlignCenterVertical className="h-5 w-5 text-gray-700" />
    const verticalCenterIcon = <AlignCenterHorizontal className="h-5 w-5 text-gray-700" />
    const centerInDesignIcon = <Shrink className="h-5 w-5 text-gray-700 rotate-45" />
    const flipHorizontalIcon = <FlipHorizontal className="h-5 w-5 text-gray-700" />
    const sendToBackIcon = <SendToBack className="h-5 w-5 text-gray-700" />
    const colorIcon = <Palette className="h-5 w-5 text-gray-700" />
    const textEditIcon = <Edit className="h-5 w-5 text-gray-700" />
    const imageIcon = <ImageIcon className="h-5 w-5 text-gray-700" />
    const deleteIcon = <Trash2 className="h-5 w-5 text-red-600" />

    const floatingBarDescription = renderToString(
      <div>
        <p className="mb-4">{getText(steps.floatingBar.description)}</p>
        <div className="grid grid-cols-[24px_1fr] gap-2">
          {horizontalCenterIcon}
          <div>
            <strong>{getText(steps.floatingBar.horizontalCenterLabel)}</strong> -
            {getText(steps.floatingBar.horizontalCenterDescription)}
          </div>
          {verticalCenterIcon}
          <div>
            <strong>{getText(steps.floatingBar.verticalCenterLabel)}</strong> -
            {getText(steps.floatingBar.verticalCenterDescription)}
          </div>
          {centerInDesignIcon}
          <div>
            <strong>{getText(steps.floatingBar.centerInDesignLabel)}</strong> -
            {getText(steps.floatingBar.centerInDesignDescription)}
          </div>
          {flipHorizontalIcon}
          <div>
            <strong>{getText(steps.floatingBar.flipHorizontalLabel)}</strong> -
            {getText(steps.floatingBar.flipHorizontalDescription)}
          </div>
          {sendToBackIcon}
          <div>
            <strong>{getText(steps.floatingBar.sendToBackLabel)}</strong> -
            {getText(steps.floatingBar.sendToBackDescription)}
          </div>
          {colorIcon}
          <div>
            <strong>{getText(steps.floatingBar.colorLabel)}</strong> -
            {getText(steps.floatingBar.colorDescription)}
          </div>
          {textEditIcon}
          <div>
            <strong>{getText(steps.floatingBar.textEditorLabel)}</strong> -
            {getText(steps.floatingBar.textEditorDescription)}
          </div>
          {imageIcon}
          <div>
            <strong>{getText(steps.floatingBar.photoReplaceLabel)}</strong> -
            {getText(steps.floatingBar.photoReplaceDescription)}
          </div>
          {deleteIcon}
          <div>
            <strong>{getText(steps.floatingBar.deleteLabel)}</strong> -
            {getText(steps.floatingBar.deleteDescription)}
          </div>
        </div>
        <p className="mt-4 italic text-sm">{getText(steps.floatingBar.note)}</p>
      </div>,
    )

    const driverObj = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps: [
        {
          element: '[data-gtm="toolbar"]',
          popover: {
            title: getText(steps.toolbar.title),
            description: toolbarDescription,
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '[data-gtm="add-to-cart-button"]',
          popover: {
            title: getText(steps.addToCart.title),
            description: getText(steps.addToCart.description),
            side: 'left',
            align: 'center',
          },
        },
        {
          element: '.floating-action-bar',
          popover: {
            title: getText(steps.floatingBar.title),
            description: floatingBarDescription,
            side: 'top',
            align: 'center',
          },
        },
      ],
      onDestroyStarted: () => {
        if (driverObj.getActiveIndex() !== driverObj.getConfig().steps!.length - 1) {
          onSkip?.()
        } else {
          onComplete?.()
        }
        driverObj.destroy()
      },
    })

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      driverObj.drive()
    }, 500)

    return () => {
      clearTimeout(timeoutId)
      driverObj.destroy()
    }
  }, [enabled, steps, onComplete, onSkip])

  return null
}
